import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { IdleTimeoutService } from '../../core/services/idle-timeout.service';

@Component({
  selector: 'app-idle-timeout-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (idle.warningOpen()) {
      <div class="idle-backdrop" role="presentation">
        <section class="idle-modal" role="dialog" aria-modal="true" aria-labelledby="idle-title">
          <div class="idle-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div class="idle-copy">
            <p class="idle-eyebrow">Session timeout</p>
            <h2 id="idle-title">You will be logged out soon</h2>
            <p>
              No activity has been detected for almost 1 hour. Stay signed in to keep working,
              or log out now.
            </p>
          </div>
          <div class="idle-countdown" aria-live="polite">{{ idle.remainingLabel() }}</div>
          <div class="idle-actions">
            <button type="button" class="idle-secondary" (click)="idle.logoutNow()">Log out</button>
            <button type="button" class="idle-primary" (click)="idle.staySignedIn()">Stay signed in</button>
          </div>
        </section>
      </div>
    }
  `,
  styles: [`
    .idle-backdrop {
      position: fixed;
      inset: 0;
      z-index: 5000;
      display: grid;
      place-items: center;
      padding: 24px;
      background: rgba(8, 20, 34, 0.54);
      backdrop-filter: blur(4px);
    }

    .idle-modal {
      width: min(100%, 460px);
      padding: 28px;
      border-radius: 16px;
      background: #ffffff;
      border: 1px solid rgba(0, 117, 201, 0.14);
      box-shadow: 0 28px 80px rgba(0, 31, 56, 0.28);
      color: #142536;
    }

    .idle-icon {
      width: 54px;
      height: 54px;
      display: grid;
      place-items: center;
      border-radius: 16px;
      color: #0075c9;
      background: #eaf6fd;
      border: 1px solid #cde9f9;
      margin-bottom: 18px;
    }

    .idle-copy {
      display: grid;
      gap: 8px;
    }

    .idle-eyebrow {
      margin: 0;
      color: #0075c9;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h2 {
      margin: 0;
      color: #0f2233;
      font-size: 24px;
      line-height: 1.15;
      font-weight: 800;
      letter-spacing: 0;
    }

    p {
      margin: 0;
      color: #5f7184;
      font-size: 15px;
      line-height: 1.55;
      font-weight: 500;
    }

    .idle-countdown {
      display: inline-grid;
      place-items: center;
      min-width: 88px;
      height: 42px;
      margin: 22px 0;
      padding: 0 14px;
      border-radius: 999px;
      background: #fff7ed;
      border: 1px solid #fed7aa;
      color: #b45309;
      font-size: 20px;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
    }

    .idle-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }

    button {
      height: 44px;
      border-radius: 10px;
      padding: 0 18px;
      font-size: 14px;
      font-weight: 800;
      cursor: pointer;
    }

    .idle-secondary {
      background: #ffffff;
      border: 1px solid #ccd9e4;
      color: #42576a;
    }

    .idle-primary {
      background: linear-gradient(90deg, #0075c9, #00bfd6);
      border: 0;
      color: #ffffff;
      box-shadow: 0 12px 24px rgba(0, 117, 201, 0.24);
    }

    @media (max-width: 520px) {
      .idle-modal { padding: 24px; }
      .idle-actions { flex-direction: column-reverse; }
      button { width: 100%; }
    }
  `],
})
export class IdleTimeoutModalComponent {
  protected readonly idle = inject(IdleTimeoutService);
}
